import Navbar from '../components/navbar';
import Footer from '../components/footer';
import '../App.scss';

const Home = () => {
    return (
		<div className="App">
			<header>
				<Navbar></Navbar>
			</header>
		  
			<body>
		<div className='padding-bottom-48 white'>
			<div className="align-left section-header white">
				<h1>How We Score</h1>		
			</div>	
				<div className='align-left padding-48 scoring'>

				<h3>The MERL Diversity Scoring Method: How We Tag, Score, and Blurb Media Texts (Short Version)</h3>

<p>Diversity Scoring is a three-step process. Those steps are: tagging, scoring, and blurbing (reviewing).  </p>
<ol>
<li className="title">Tagging </li>

<p>Tagging is an in-depth research and data collection process with three primary focal points: reception, bias, and characters.  </p>
<p>We watch a TV show or movie and extensively research its audience reception online, looking for praise or criticism of how the text depicts minority characters. This involves reading published reviews and think pieces from sources like <i>The New York Times</i> and combining viewer discussion on social media sites like X or review sites like Rotten Tomatoes.  </p>
<p>We also evaluate the text for bias. We maintain an evolving list of “bias tags,” or specific types of negative representational tropes and devices we look for, such as T for tokenism when we feel that a minority character has been included only for diversity brownie points.  </p>
<p>Finally, we research the identities of the text’s cast and characters. We identify up to 8 significant characters in the text and tag, in as much detail as we can find, information about both the actor and character’s ethnicity, nationality, gender, sexuality, and age.  </p>

<li className="title">Scoring</li>

<p>After we finish tagging a text, we use the tags to assign the text a numerical score out of 100.</p>
<p>More precisely, we give the text two scores out of 50 –  the bias and reception score and the cast and character score –  and add those two scores together. For reference, a text that performs well will probably get a total score in the 60s.  </p>
<p>The bias and reception score is calculated by tallying up a text’s bias tags and reception tags and evaluating them according to a rubric we’ve developed. The rubric has a row each for ethnicity, nationality, gender, sexuality, and age.  </p>
<p>The cast and character score measures how much the cast of a text varies from the cultural “norm” of straight, white, cis male characters. For example, a text is awarded points for ethnicity based on what percentage of the cast are people of color.  </p>

<li className="title">Blurbing</li>

<p>In the final step, we write an approximately 500-word review of the text, in which we summarize our opinions, and broader audience opinion, of how the text depicts minority characters and their storylines.</p>
</ol>

<p></p>

<h3>What about these birds?</h3>		

<p> The mascot of MERL is the merl bird. Each text has a bird that represents how well it did in diversity scoring:</p>

<div className="card-highlight padding-12">
			<div className='flex-col gap-12'>
				<div className='flex-item gap-24 align-top'>
					<div className="flex-col gap-12 gray-primary-text">
						<div>
							<img src={require('../components/images/score-green.png')} alt="soaring green bird" className='score-bird'></img>
				<div className={'score-value score-green'}>62</div>
						</div>
						
					</div>
					<div>
						<h2>Flying Bird</h2>
						<p>Great job! A soaringly diverse text!</p>
					</div>

				</div>
			</div>
</div>
<div className="card-highlight padding-12">
			<div className='flex-col gap-12'>
				<div className='flex-item gap-24 align-top'>
					<div className="flex-col gap-12 gray-primary-text">
						<div>
							<img src={require('../components/images/score-yellow.png')} alt="yellow bird giving a thumbs-up" className='score-bird'></img>
							<div className={'score-value score-yellow'}>44</div>
						</div>
					</div>
					<div>
						<h2>Thumbs-Up Bird</h2>					
						<p>Not bad! Could be better, but could be worse.</p>
					</div>

				</div>
			</div>
</div>
	

<div className="card-highlight padding-12">
			<div className='flex-col gap-12'>
				<div className='flex-item gap-24 align-top'>
					<div className="flex-col gap-12 gray-primary-text">
						<div>
							<img src={require('../components/images/score-orange.png')} alt="orange bird side-eyeing" className='score-bird'></img>
				<div className={'score-value score-orange'}>38</div>
						</div>
						
					</div>

					<div>
						<h2>Side-Eye Bird</h2>
						<p>Meh. We can do better than this.</p>
					</div>

				</div>
			</div>
</div>

<div className="card-highlight padding-12">
			<div className='flex-col gap-12'>
				<div className='flex-item gap-24 align-top'>
					<div className="flex-col gap-12 gray-primary-text">
						<div>
							<img src={require('../components/images/score-red.png')} alt="dead red bird lying on the ground" className='score-bird'></img>
				<div className={'score-value score-red'}>21</div>
						</div>
						
					</div>

					<div>
						<h2>Dead Bird</h2>
						<p>Boo. We're dead with how un-diverse this text is. </p>
					</div>

				</div>
			</div>
</div>

	


<h3>The MERL Diversity Scoring Method: How We Tag, Score, and Blurb Media Texts (Long Version)</h3>

<p>The MERL Diversity Scores method is also called the TSB method, which stands for Tagging-Scoring-Blurbing.</p>
<p>MERL currently tags films and television series. We refer to a film or TV series as “a media text” or just “a text.”</p>
<p>Two MERL researchers tag, score, and blurb (TSB) every media text because we believe that no single person’s evaluation of the representation of diverse groups in a text would be sufficient. By presenting two TSBs for each text, we communicate that multiple perspectives on diversity are valuable and necessary.</p>
<ol>
<li className="title">Step 1: We <u>Tag</u> the Text</li>

<p>First, we tag basic information about the text, such as title, year, production company, etc.</p>
<p>Second, we assess whether the text warrants any “Bias Tags.” The possible bias tags are:</p>
<ul>
<li><b>A =</b> ableism</li>

<li><b>B =</b> fails <a href="https://www.backstage.com/magazine/article/what-is-the-bechdel-test-75534/">Bechdel Test</a></li>

<li><b>C =</b> colorism</li>
<li><b>D =</b> fails <a href="https://twitter.com/duvernaytestorg?lang=en">DuVernay Test</a></li>
<li><b>E =</b> ethnic impersonation</li>
<li><b>F =</b> fetishization/sexualization of WOC</li>
<li><b>Q =</b> queer impersonation</li>
<li><b>S =</b> stereotyping of woman or minority character</li>
<li><b>T =</b> tokenized minority character</li>
<li><b>V =</b> minority characters only further violence (are violent or have violence enacted on them)</li>
<li><b>W =</b> white only</li>
<li><b>Y =</b> young (no female cast leads 50 or above)</li>
<li>* <b>=</b> contested tags (e.g., C*)</li>
</ul>
<p>Explanation of Bias Tags:</p>
<ul>
<li><b>A</b><br/> Ableism. While ableism takes many forms, this tag denotes one or more of the following scenarios:<br/> 1) A character with a disability is played by an actor who does not share that disability. In the case of visible disabilities, make-up or special effects may be used to simulate the disability.<br/> 2) A character is depicted as villainous because of their disability.<br/> 3) Disabled characters have dream or fantasy sequences where they “lose” their disabilities or otherwise idealize being or becoming non-disabled.
</li>
<li><b>B</b><br/> Fails Bechdel Test. If the media property does not (1) have two or more female cast leads that (2) talk to each other about (3) something other than men. Further <a href="https://en.wikipedia.org/wiki/Bechdel_test">reading</a>.
</li>
<li><b>C</b><br/> Colorism. This tag denotes one or more of the following scenarios:<br/> 1) Either there are no dark-skinned actors or the characters played by dark-skinned actors are not given meaningful storylines.<br/> 2) The lead women of color characters are played by light-skinned actors.<br/> 3) For adaptations and remakes: A character who is dark-skinned in the source or original or antecedent material is played by a light-skinned actor in the adaptation or remake.<br/> 4) Antagonists are played by dark-skinned actors, while protagonists are played by light-skinned actors.
</li>
<li><b>D</b><br/> Fails DuVernay Test. If the media property has no cast leads with ethnicity values other than “white” that have fully-realized lives. Instead, these cast leads either don’t exist or, if a BIPOC cast lead exists, it serves as scenery in white stories.
</li>
<li><b>E</b><br/> Ethnic Impersonation. If the media property has actors of one ethnicity playing characters of another ethnicity. This includes offensive practices such as blackface, yellowface, etc. This does not refer to colorblind and race-conscious casting practices in which actors of color play historically white roles.
</li>
<li><b>F</b><br/> Heavily Fetishizes or Sexualizes BIPOC Women and Girls. If the media property has BIPOC female characters but presents them as objects of a sexualized gaze, particularly in racialized ways.
</li>
<li><b>Q</b><br/> Queer Impersonation. If the media property has actors of one gender playing characters of another gender or actors of one sexual identity playing characters of another sexual identity. This mostly refers to straight actors playing queer characters or cis actors playing trans characters. This does not refer to queer actors playing straight characters, trans actors playing cis characters, drag performers, or characters in disguise.
</li>
<li><b>S</b><br/> Stereotyping of Minority or Female Character. If the media property has minority or female characters, but their portrayals simply conform to both negative and positive stereotypes.
</li>
<li><b>T</b><br/> Tokenized Minority Character. If the media property has minority characters but their presence serves only to create a general impression of diversity. These characters may appear as sidekicks to the main characters but do not have fully realized lives or a substantial storyline of their own.
</li>
<li><b>V</b><br/> Victim or Perpetrator of Violence. If the media property has minority characters but they appear only to further violent storylines.
</li>
<li><b>W</b><br/> White Only. If the media property has no cast leads with ethnicity values other than “white.”
</li>
<li><b>Y</b><br/> Young. If the media property has no female cast leads with age values 50 or above or if the media property has actors under age 50 playing characters 50 or above.
</li>
<li><b>*</b><br/> Contested. If one of the rulesets above has a variety of opinions and can not be definitively determined, e.g., S*.
</li>
</ul>
<p>Next, we tag audience reception of the text.</p>
<p>The goal of reception tagging is to capture the conversation around a text’s representation of diversity as comprehensively as possible.</p>
<p>Bias and reception tagging often capture similar data. The distinction is that <strong>bias tags reflect the presence or absence of bias in the text itself</strong> (as determined by the tagger), and <strong>reception tags reflect audience response</strong>.</p>
<p><strong>What counts as reception?</strong></p>
<ul>
<li>Critical reception: published reviews written by professional reviewers. We check the following sources for critical reception:  <ul>
<li><i>New York Times</i>, <i>Washington Post</i>, <i>Vox</i>, <i>Vulture</i>, etc.  </li>
<li>“Reception” section on the text’s Wikipedia page  </li>
<li>We google “[title] reviews”  </li>
</ul>
</li>
<li>The Discourse: general audience discussion on social media, review sites, etc., which often results in some form of consensus. We check the following sources for The Discourse:   <ul>
<li>Letterboxd, Rotten Tomatoes, IMDB, etc.   </li>
<li>Twitter, Reddit, TikTok, etc.  </li>
<li>We google phrases such as “[name of text] fan reactions” or “[name of text] controversy”  </li>
<li><a href="https://tvtropes.org/pmwiki/pmwiki.php/Series/WandaVision">TVtropes.org</a> (particularly the YMMV tab for any particular text)  </li>
</ul>
</li>
<li>Fringe reception: small pockets of strong and vocal reception that may run contrary to critical reception or The Discourse. However, we only log fringe reception if we find evidence of a critical mass, not just one very noisy person’s opinion.  <ul>
<li>We sometimes tag fringe reception that is fringe vis-à-vis MERL’s values (e.g., people who think a Black Little Mermaid is bad because they&#39;re racist). In general, we do not factor this type of fringe reception into our scoring, but we may mention it in our blurbs.</li>
</ul>
</li>
</ul>

<li className="title">Step 2: We <u>Tag</u> the Main Characters (up to 8)</li>

<p>Each MERL researcher decides who the top (most important) 8 characters are in the text. They can also choose to tag fewer than 8 characters.</p>
<p>For each character, we tag the following metadata: Name, Ethnicity, Gender, Sexuality, Age (by decile, e.g., 10-19; 20-29; 30-39; etc.), and Nationality. For sci-fi, fantasy, or other genres in which identities and orientations exist that do not exist in reality, we use the designator “X,” i.e., Ethnicity X, Gender X, Nationality X. Tagging any metadata category “Unknown” is also an option.</p>

<li className="title">Step 3: We <u>Score</u> the Media Text</li>


<h4>Part 1: Qualitative Score</h4>
<p>We calculate the qualitative score for a media text based on its reception tagging. We rate the reception that the text received in Ethnicity, Gender, Sexuality, Age, and Nationality on a scale of 1-5:</p>
<p>1 = lowest (highly critical reception)</p>

<p>5 = highest (highly positive reception)</p>

<p>If there is no evidence of reception, we give a score of 3.</p>
<p>If there is a bias tag pertaining to the reception category, we do not give a score higher than 3.</p>
<p>We then add the scores for each category, for a maximum of 25 points (5 points max for 5 categories).</p>
<p>Then, we multiply this score by 2, to arrive at a score out of 50 (rather than 25).</p>
<p>This is the qualitative score.</p>

<h4>Part 2: Quantitative Score</h4>
<p>We calculate the quantitative score for a media text by counting how many characters (out of the top 8 we tagged) represent a minority in each identity category.</p>
<p><b>Ethnicity</b></p>
<ul>
<li>Number of characters (out of the max 8 characters tagged) who are ethnicities other than “white”:</li>
<li>Out of how many characters tagged e.g., 4/8):</li>
<li>Percentage (4/8=50%):</li>
<li>Convert percentage to points (50% = 5 points): </li>
</ul>
<p><b>Gender</b></p>
<ul>
<li>Number of characters who are genders other than “cis-men”:</li>
<li>Out of how many characters tagged (e.g.., 4/8):</li>
<li>Percentage (4/8=50%):</li>
<li>Convert percentage to points (50% = 5 points):</li>
</ul>
<p><b>Sexuality</b></p>
<ul>
<li>Number of characters who are sexualities other than “straight”:</li>
<li>Out of how many characters tagged (i.e., 4/8):</li>
<li>Percentage (4/8=50%):</li>
<li>Convert percentage to points (50% = 5 points):</li>
</ul>
<p><b>Age</b></p>
<ul>
<li>Number of characters who are aged 50 or older:</li>
<li>Out of how many characters tagged (i.e., 4/8):</li>
<li>Percentage (4/8=50%):</li>
<li>Convert percentage to points (50% = 5 points):</li>
</ul>
<p><b>Nationality</b></p>
<ul>
<li>Number of characters who are from Low- and Middle-Income Countries:</li>
<li>Out of how many characters tagged (i.e., 4/8):</li>
<li>Percentage (4/8=50%):</li>
<li>Convert percentage to points (50% = 5 points):</li>
</ul>

<p>We then add all of the points for the Total Quantitative Score.</p>
<h4>Part 3: Total Diversity Score</h4>
<p>Next, we add the Qualitative and Quantitative Scores.</p>
<p>The sum is the Total Diversity Score (possible points: 100).</p>
<li className="title">Step 4: We <u>Blurb</u> the Media Text</li>
<p>After tagging and scoring a text, each MERL researcher writes a 250-500-word review of the representation of diversity in the text. Our goal is not to write in the genre of academic essays, but in the vein of cultural criticism, written in the first person with strong points of view. Our blurbs provide an overall review of the text’s successes and shortcomings with respect to diversity and provide justifications for our scores when necessary.</p>
</ol>

				
				</div>
				</div>
				
				<Footer/>
			</body>
		</div>
	  );
}

export default Home;
